import React from 'react';
import Tooltip from './Tooltip';
import type { AvailableTransition } from '../types/playbook.types';

interface TransitionButtonProps {
  transition: AvailableTransition;
  isAdmin: boolean;
  onExecute: (transitionId: string) => void;
  onForce: (transitionId: string) => void;
}

const BUTTON_STYLE_MAP: Record<string, string> = {
  default: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700',
  primary: 'bg-lex-accent text-lex-accent-contrast hover:bg-lex-accent-hover',
  success: 'bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600',
  warning: 'border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200 dark:hover:bg-amber-900/50',
  danger: 'bg-rose-600 text-white hover:bg-rose-700 dark:bg-rose-700 dark:hover:bg-rose-600',
};

const TransitionButton: React.FC<TransitionButtonProps> = ({ transition, isAdmin, onExecute, onForce }) => {
  const label = transition.buttonLabel || transition.name;

  if (!transition.isAllowed) {
    return (
      <div className="inline-flex items-center gap-1.5">
        <Tooltip content={transition.blockedReason || 'This transition is currently blocked'}>
          <button
            type="button"
            disabled
            className="rounded-md px-3 py-1.5 text-sm font-medium border border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed dark:border-gray-600 dark:bg-gray-700 dark:text-gray-500"
          >
            {label}
          </button>
        </Tooltip>
        {isAdmin && (
          <button
            type="button"
            onClick={() => onForce(transition.transitionId)}
            className="rounded-md px-2 py-1.5 text-xs font-semibold border border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-700 dark:text-rose-300 dark:hover:bg-rose-900/30"
          >
            Force
          </button>
        )}
      </div>
    );
  }

  const styleClasses = BUTTON_STYLE_MAP[transition.buttonStyle] || BUTTON_STYLE_MAP.primary;

  return (
    <button
      type="button"
      onClick={() => onExecute(transition.transitionId)}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${styleClasses}`}
    >
      {label}
    </button>
  );
};

export default TransitionButton;
